import * as LucideIcons from 'lucide-react';
import { GAME_CATEGORIES } from '../../types/game';

interface CategoryTagProps {
  categoryId: string;
  size?: 'sm' | 'md';
}

// Category colors mapping
const getCategoryColor = (categoryId: string): { bg: string; text: string; bgDark: string; textDark: string } => {
  const colorMap: Record<string, { bg: string; text: string; bgDark: string; textDark: string }> = {
    action: { bg: 'bg-red-100', text: 'text-red-700', bgDark: 'dark:bg-red-900/30', textDark: 'dark:text-red-300' },
    adventure: { bg: 'bg-amber-100', text: 'text-amber-700', bgDark: 'dark:bg-amber-900/30', textDark: 'dark:text-amber-300' },
    arcade: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', bgDark: 'dark:bg-fuchsia-900/30', textDark: 'dark:text-fuchsia-300' },
    board: { bg: 'bg-orange-100', text: 'text-orange-700', bgDark: 'dark:bg-orange-900/30', textDark: 'dark:text-orange-300' },
    card: { bg: 'bg-violet-100', text: 'text-violet-700', bgDark: 'dark:bg-violet-900/30', textDark: 'dark:text-violet-300' },
    casino: { bg: 'bg-yellow-100', text: 'text-yellow-700', bgDark: 'dark:bg-yellow-900/30', textDark: 'dark:text-yellow-300' },
    casual: { bg: 'bg-sky-100', text: 'text-sky-700', bgDark: 'dark:bg-sky-900/30', textDark: 'dark:text-sky-300' },
    creative: { bg: 'bg-pink-100', text: 'text-pink-700', bgDark: 'dark:bg-pink-900/30', textDark: 'dark:text-pink-300' },
    educational: { bg: 'bg-blue-100', text: 'text-blue-700', bgDark: 'dark:bg-blue-900/30', textDark: 'dark:text-blue-300' },
    family: { bg: 'bg-green-100', text: 'text-green-700', bgDark: 'dark:bg-green-900/30', textDark: 'dark:text-green-300' },
    music: { bg: 'bg-purple-100', text: 'text-purple-700', bgDark: 'dark:bg-purple-900/30', textDark: 'dark:text-purple-300' },
    party: { bg: 'bg-rose-100', text: 'text-rose-700', bgDark: 'dark:bg-rose-900/30', textDark: 'dark:text-rose-300' },
    puzzle: { bg: 'bg-indigo-100', text: 'text-indigo-700', bgDark: 'dark:bg-indigo-900/30', textDark: 'dark:text-indigo-300' },
    racing: { bg: 'bg-cyan-100', text: 'text-cyan-700', bgDark: 'dark:bg-cyan-900/30', textDark: 'dark:text-cyan-300' },
    role_playing: { bg: 'bg-emerald-100', text: 'text-emerald-700', bgDark: 'dark:bg-emerald-900/30', textDark: 'dark:text-emerald-300' },
    simulation: { bg: 'bg-teal-100', text: 'text-teal-700', bgDark: 'dark:bg-teal-900/30', textDark: 'dark:text-teal-300' },
    sports: { bg: 'bg-lime-100', text: 'text-lime-700', bgDark: 'dark:bg-lime-900/30', textDark: 'dark:text-lime-300' },
    strategy: { bg: 'bg-slate-100', text: 'text-slate-700', bgDark: 'dark:bg-slate-900/30', textDark: 'dark:text-slate-300' },
    trivia: { bg: 'bg-blue-100', text: 'text-blue-700', bgDark: 'dark:bg-blue-900/30', textDark: 'dark:text-blue-300' },
    word: { bg: 'bg-indigo-100', text: 'text-indigo-700', bgDark: 'dark:bg-indigo-900/30', textDark: 'dark:text-indigo-300' },
  };
  return colorMap[categoryId] || { bg: 'bg-gray-100', text: 'text-gray-700', bgDark: 'dark:bg-gray-900/30', textDark: 'dark:text-gray-300' };
};

// Get category icon
const getCategoryIcon = (categoryId: string) => {
  const category = GAME_CATEGORIES.find(c => c.id === categoryId);
  if (category && category.icon) {
    // @ts-ignore - Dynamic icon lookup
    const Icon = LucideIcons[category.icon];
    return Icon || LucideIcons.Gamepad2;
  }
  return LucideIcons.Gamepad2;
};

// Get category display name
const getCategoryName = (categoryId: string) => {
  const category = GAME_CATEGORIES.find(c => c.id === categoryId);
  return category?.displayName || categoryId;
};

export default function CategoryTag({ categoryId, size = 'sm' }: CategoryTagProps) {
  const IconComponent = getCategoryIcon(categoryId);
  const colors = getCategoryColor(categoryId);
  const displayName = getCategoryName(categoryId);

  if (size === 'sm') {
    return (
      <span
        className={`px-2 py-0.5 rounded-md text-xs font-medium flex items-center gap-1 ${colors.bg} ${colors.text} ${colors.bgDark} ${colors.textDark}`}
      >
        <IconComponent className="w-3 h-3" />
        {displayName}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${colors.bg} ${colors.text} ${colors.bgDark} ${colors.textDark}`}
    >
      <IconComponent className="w-3.5 h-3.5" />
      {displayName}
    </span>
  );
}
